import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from '../../../shared/utils.service';
import { RBACService } from '../../../shared/rbac.service';
import { EmployeesSelectorComponent } from '../../../employees/employees-selector/employees-selector.component';
import { RbacPermissionsSelectorComponent } from '../rbac-permissions-selector/rbac-permissions-selector.component';
import { RbacRolesFilters } from './rbac-roles-filters.interface';

/**
 * The filter dialog for the RBAC roles list. Roles can be filtered by the permissions they grant and
 * by the employees assigned to them.
 */
@Component({
  selector: 'app-rbac-roles-filter',
  templateUrl: './rbac-roles-filter.component.html',
  styleUrls: ['./rbac-roles-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacRolesFilterComponent implements OnInit {
  // Set default filter values.
  filters: RbacRolesFilters = {
    rbac_permission_ids: [],
    user_ids: []
  } as RbacRolesFilters;

  constructor(
    public utils: UtilsService,
    public rbac: RBACService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RbacRolesFilterComponent>
  ) {}

  ngOnInit(): void {
    // Seed the filters from the values the list is currently using.
    this.filters.rbac_permission_ids = this.data['rbac_permission_ids'] || [];
    this.filters.user_ids = this.data['user_ids'] || [];
  }

  /**
   * Opens the RBAC permissions selector and stores the selected permission ids.
   *
   * @returns {void}
   */
  onSelectPermissions(): void {
    this.utils.showComponentDialog(
      RbacPermissionsSelectorComponent,
      {
        selected: this.filters.rbac_permission_ids
      },
      {
        width: '1200px'
      },
      (results: number[]): void => {
        if (typeof results === 'undefined') {
          return;
        }
        this.filters.rbac_permission_ids = results;
      }
    );
  }

  /**
   * Opens the employees selector and stores the selected user ids.
   *
   * @returns {void}
   */
  onSelectUsers(): void {
    this.utils.showComponentDialog(
      EmployeesSelectorComponent,
      {
        selected: this.filters.user_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]): void => {
        if (typeof results === 'undefined') {
          return;
        }
        this.filters.user_ids = results;
      }
    );
  }

  /**
   * Clears all filter values and closes the dialog.
   *
   * @returns {void}
   */
  onClearAndClose(): void {
    this.filters.rbac_permission_ids = [];
    this.filters.user_ids = [];
    this.onSaveAndClose();
  }

  /**
   * Saves the filters and closes the dialog.
   *
   * @returns {void}
   */
  onSaveAndClose(): void {
    this.dialogRef.close(this.filters);
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}
